// @flow
import React from 'react';
import classnames from 'classnames';
import type { List } from 'immutable';
import { Box, Flex, Stack } from '@locus-taxy/neo-ui';
import theme from './baseFpPlanViewFactory.scss';
import { type TFunction } from 'react-i18next';

import { Loader } from 'common/components/core/loader/Loader';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import MoveVisitDialog from './MoveVisitDialog';
import FpPlanViewFilterBar from './FpPlanViewFilterBar';
import { type FpSalesbeatPlan } from 'common/models/flow/FpSalesbeatPlan';
import { type FpBeat } from 'common/models/internal/FpBeat';
import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import type {
    FpAllocatedMapOptionsType,
    FpPlanViewPagination
} from './FpAllocatedVisitsContainer';
import { type BeatPlanEdits } from 'common/models/internal/BeatPlanEdits';
import XyzzyConfigDialog from 'common/components/common/XyzzyConfigDialog';
import {
    ColumnResizer,
    useColumnResizer
} from 'common/components/core/ColumnResizer';

export type TaskReference = {
    taskId: string,
    tourId: string
};
//  made props inexact because its a view generating factory used by multiple containers
type Props = {
    updateAsMapMoves: boolean,
    setUpdateAsMapMoves: (updateAsMapMoves: boolean) => void,
    status: string,
    isTableDataLoading: boolean,
    toggleMoveVisitDialog: () => void,
    onSelectAllToggle: () => void,
    isAllVisibleBeatsSelected: boolean,
    isMoveVisitDialogVisible: boolean,
    planEdits: Array<BeatPlanEdits>,
    onMouseLeave: () => void,
    salesbeatPlan: FpSalesbeatPlan,
    onVisitHover: (beatId: string, visitId: string) => void,
    onVisitHoverOutside: () => void,
    hoveredBeat: string,
    currentHoveredVisit: string,
    zoomLevel: number,
    getAllFilters: () => void,
    selectedFilters: Map<string, Array<Object>>,
    removeFilterItem: (filterItem: string) => void,
    sortBeats: (sortOption: string, sortPreference: string) => void,
    resetSort: () => void,
    filteredBeats: List<FpBeat>,
    onSearchInputChange: (value: string) => void,
    salesCategoryColorMap: Map<string, string>,
    riderIdColorMap: Map<string, string>,
    selectedSortOrder: ?string,
    selectedSortOption: ?string,
    getSalesmanOptions: () => Array<{ value: string, label: string }>,
    openMoveVisitDialog: () => void,
    onUnallocateVisit: (visitId: string) => void,
    onZoomLevelChanged: (newZoomLever: number) => void,
    contextMenu: Object,
    onContextMenuOpen: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    onContextMenuOpenMap: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    onContextMenuClose: () => void,
    contextMenuOpen: boolean,
    onVisitClick: (beatId: string, visitId: string) => void,
    onVisitDeselect: () => void,
    selectedVisit: string,
    pjPlan: FpPjPlan,
    beatSelectionMap: Map<string, boolean>,
    applyFilters: () => void,
    onClearFilter: () => void,
    toggleBeatSelection: () => void,
    visibleBeats: Array<FpBeat>,
    onPageChange: (newPage: number) => void,
    onPageSizeChange: newPageSize => void,
    onEditBeat: (targetBeat: string, visitId: string) => void,
    paginationDetails: FpPlanViewPagination,
    selectedBeat: string,
    beats: Array<FpBeat>,
    mapOptions: FpAllocatedMapOptionsType,
    getContainer: () => string,
    onOptionsChange: FpAllocatedMapOptionsType => void,
    t: TFunction,
    screenSizeState: string,
    ...
};

const baseFpPlanViewFactory = ({
    TableView,
    ChartView,
    MapView,
    ActionsBar,
    ZoomSlider
}): React.Node => {
    class FpBaseContainer extends React.Component<Props> {
        parentElRefForMarker = React.createRef<HTMLDivElement>();

        renderLoading() {
            return (
                <Box height="fluid">
                    <Loader active theme={theme} />
                </Box>
            );
        }

        renderChartView() {
            if (!ChartView) {
                return null;
            }
            const { isTableDataLoading, salesbeatPlan, ...others } = this.props;

            if (isTableDataLoading) {
                return this.renderLoading();
            }

            if (!salesbeatPlan) {
                return null;
            }
            return (
                <ChartView
                    {...others}
                    salesbeatPlan={salesbeatPlan}
                    isTableDataLoading={isTableDataLoading}
                />
            );
        }

        renderTableView() {
            if (!TableView) {
                return null;
            }
            const {
                isTableDataLoading,
                getContainer,
                planEdits,
                onContextMenuClose,
                onContextMenuOpen,
                contextMenu,
                ...rest
            } = this.props;

            return (
                <div className={classnames(theme.summaryChartContainer)}>
                    <TableView
                        {...rest}
                        isTableDataLoading={isTableDataLoading}
                        getContainer={getContainer}
                        planEdits={planEdits}
                        onContextMenuClose={onContextMenuClose}
                        onContextMenuOpen={onContextMenuOpen}
                        contextMenu={contextMenu}
                    />
                </div>
            );
        }

        renderSecondaryNavigation() {
            if (!ZoomSlider) {
                return null;
            }
            return (
                <Box
                    height="32px"
                    className={theme.secondaryNavigationContainer}
                >
                    <Box ml={'1px'} height="100%">
                        {this.renderZoomSlider()}
                    </Box>
                </Box>
            );
        }

        renderZoomSlider() {
            if (!ZoomSlider) {
                return null;
            }
            const { zoomLevel, onZoomLevelChanged } = this.props;
            return (
                <Box>
                    <ZoomSlider
                        zoomLevel={zoomLevel}
                        onZoomLevelChanged={onZoomLevelChanged}
                    />
                </Box>
            );
        }

        render() {
            const {
                // Column Resizer Props
                rightPaneWidth,
                leftPaneWidth,
                columnResizerPosition,
                screenSizeState,
                setScreenSizeState,
                onColumnResize,
                onColumnResizeStop,
                nextScreenSizeState,
                isResizing,
                isMoveVisitDialogVisible,
                getAllFilters,
                changeVisitsView,
                getContainer,
                filteredBeats,
                onSearchInputChange,
                filters,
                beats,
                pjPlan,
                applyFilters,
                paginationDetails,
                onPageChange,
                onPageSizeChange,
                sortBeats,
                resetSort,
                selectedSortOption,
                selectedSortOrder,
                selectedVisitsView,
                outletSalesCategories,
                onClearFilter,
                selectedFilters,
                removeFilterItem,
                t,
                toggleMoveVisitDialog,
                edits
            } = this.props;
            const allFilters = getAllFilters ? getAllFilters() : null;
            return (
                <Stack height="fluid">
                    <Box>
                        <ActionsBar
                            changeVisitsView={changeVisitsView}
                            onSearchInputChange={onSearchInputChange}
                            filters={filters}
                            selectedVisitsView={selectedVisitsView}
                            beats={beats}
                            getContainer={getContainer}
                            pjPlan={pjPlan}
                            applyFilters={applyFilters}
                            paginationDetails={paginationDetails}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            sortBeats={sortBeats}
                            resetSort={resetSort}
                            selectedSortOption={selectedSortOption}
                            selectedSortOrder={selectedSortOrder}
                            outletSalesCategories={outletSalesCategories}
                            onClearFilter={onClearFilter}
                            selectedFilters={selectedFilters}
                            getAllFilters={getAllFilters}
                            t={t}
                            edits={edits}
                        />
                    </Box>

                    {allFilters && allFilters.length ? (
                        <FpPlanViewFilterBar
                            removeFilterItem={removeFilterItem}
                            getAllFilters={getAllFilters}
                            onClearFilter={onClearFilter}
                            filteredBeats={filteredBeats}
                            paginationDetails={paginationDetails}
                            t={t}
                        />
                    ) : null}
                    <Box className={classnames(theme.container)}>
                        <Flex
                            flexDirection="column"
                            className={theme.navigationContainer}
                        >
                            {this.renderSecondaryNavigation()}

                            <Box height="fluid" sx={{ width: leftPaneWidth }}>
                                {this.renderChartView()}
                                {this.renderTableView()}
                            </Box>
                        </Flex>

                        <ColumnResizer
                            setScreenSizeState={setScreenSizeState}
                            screenSizeState={screenSizeState}
                            columnResizerPosition={columnResizerPosition}
                            onColumnResize={onColumnResize}
                            onColumnResizeStop={onColumnResizeStop}
                            isResizing={isResizing}
                            nextScreenSizeState={nextScreenSizeState}
                            t={t}
                        />
                        <div
                            ref={this.parentElRefForMarker}
                            className={theme.map}
                            style={{ width: rightPaneWidth }}
                        >
                            <MapView
                                {...this.props}
                                parentElRefForMarker={this.parentElRefForMarker}
                            />
                        </div>
                    </Box>
                    <XyzzyConfigDialog
                        value={JSON.stringify(
                            JSON.parse(
                                pjPlan.configuration.serializedSchedulingConfig
                            ),
                            null,
                            4
                        )}
                    />
                    {isMoveVisitDialogVisible ? (
                        <Box className={theme.overlays}>
                            <OverlayDialog
                                active={isMoveVisitDialogVisible}
                                theme={theme}
                                dialogClassName={theme.moveBeatDialogue}
                            >
                                <MoveVisitDialog
                                    {...this.props}
                                    theme={theme}
                                    onDismiss={toggleMoveVisitDialog}
                                />
                            </OverlayDialog>
                        </Box>
                    ) : null}
                </Stack>
            );
        }
    }

    return function FpBaseContainerWithHooks(props) {
        const columnResizerProps = useColumnResizer('fp-plan-resizer');

        return <FpBaseContainer {...props} {...columnResizerProps} />;
    };
};

export default baseFpPlanViewFactory;
